import React from 'react';
import { Card } from '../ui/card';
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription,
} from '../ui/drawer';
import { Snowflake, Play, Clock, ChevronRight, Trash2, Pause, ClipboardList, X } from 'lucide-react';

/**
 * Drawer de Produção — mostra todas as operações/tarefas ativas do operador:
 * 1. Produção Ativa (em curso ou pausada)
 * 2. Tarefas Pontuais em curso
 * 3. Operações Congeladas
 */
export default function CongeladasDrawer({
  open,
  onOpenChange,
  operacoesCongeladas = [],
  onDescongelar,
  onDescartar,
  modoTestes = false,
  // Novos props
  producaoAtiva = null,
  producaoTempo = 0,
  onIrProducaoAtiva,
  tarefaPontualTimer = null,
  tarefaPontualTempo = 0,
  tarefaPontualPaused = false,
  onIrTarefaPontual,
}) {
  const isStale = (op) => {
    return op.of_numero === 'N/A' || op.operacao_nome === 'N/A' || !op.of_numero;
  };

  const formatTempo = (s) => {
    const h = String(Math.floor(s / 3600)).padStart(2, '0');
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
    const sec = String(s % 60).padStart(2, '0');
    return `${h}:${m}:${sec}`;
  };

  const temAlgo = producaoAtiva || tarefaPontualTimer || operacoesCongeladas.length > 0;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[80vh]" style={{ paddingBottom: '80px' }}>
        <DrawerHeader className="border-b border-stone-100 pb-4">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-xl font-heading text-navy flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Producao
            </DrawerTitle>
            <button
              onClick={() => onOpenChange(false)}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-stone-100 transition-colors"
              data-testid="close-drawer-btn"
            >
              <X className="h-5 w-5 text-stone-500" />
            </button>
          </div>
          <DrawerDescription className="text-stone-500">
            Operacoes em curso, pausadas e congeladas
          </DrawerDescription>
        </DrawerHeader>

        <div className="p-4 space-y-4 overflow-y-auto max-h-[65vh]">

          {/* Secção 1: Produção Ativa */}
          {producaoAtiva && (
            <div>
              <p className="text-xs font-bold uppercase text-stone-400 tracking-wider mb-2 flex items-center gap-1.5">
                <Play className="h-3.5 w-3.5 text-emerald-500" />
                Operacao em Curso
              </p>
              <Card
                className="border-l-4 border-l-emerald-500 bg-gradient-to-r from-emerald-50 to-white hover:shadow-lg cursor-pointer transition-all"
                onClick={() => { onIrProducaoAtiva && onIrProducaoAtiva(); onOpenChange(false); }}
                data-testid="producao-ativa-drawer-card"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-navy text-lg">
                        OF {producaoAtiva.registo?.of_numero || producaoAtiva.of_numero || '?'}
                      </p>
                      <p className="text-sm text-stone-600">
                        {producaoAtiva.registo?.cliente || producaoAtiva.cliente || ''} · {producaoAtiva.registo?.artigo || producaoAtiva.artigo || ''}
                      </p>
                      <p className="text-xs text-stone-500 mt-1">
                        {producaoAtiva.registo?.operacao_codigo || ''} {producaoAtiva.registo?.operacao_nome || producaoAtiva.operacao_nome || ''}
                        {producaoAtiva.registo?.lote_codigo ? ` · Lote ${producaoAtiva.registo.lote_codigo}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {producaoTempo > 0 && (
                        <span className="font-mono font-bold text-sm text-emerald-700">
                          {formatTempo(producaoTempo)}
                        </span>
                      )}
                      <span className="px-2 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
                        Em curso
                      </span>
                      <ChevronRight className="h-5 w-5 text-stone-400" />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Secção 2: Tarefa Pontual em Curso */}
          {tarefaPontualTimer && (
            <div>
              <p className="text-xs font-bold uppercase text-stone-400 tracking-wider mb-2 flex items-center gap-1.5">
                <ClipboardList className="h-3.5 w-3.5 text-violet-500" />
                Tarefa Pontual {tarefaPontualPaused ? 'em Pausa' : 'em Curso'}
              </p>
              <Card
                className={`border-l-4 ${tarefaPontualPaused ? 'border-l-amber-400 bg-gradient-to-r from-amber-50 to-white' : 'border-l-violet-500 bg-gradient-to-r from-violet-50 to-white'} hover:shadow-lg cursor-pointer transition-all`}
                onClick={() => { onIrTarefaPontual && onIrTarefaPontual(); onOpenChange(false); }}
                data-testid="tarefa-pontual-drawer-card"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-navy text-lg">
                        {tarefaPontualTimer.nome || tarefaPontualTimer.titulo || 'Tarefa Pontual'}
                      </p>
                      {tarefaPontualTimer.descricao && (
                        <p className="text-sm text-stone-600 truncate max-w-[250px]">
                          {tarefaPontualTimer.descricao}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-sm" style={{ color: tarefaPontualPaused ? '#D97706' : '#7C3AED' }}>
                        {formatTempo(tarefaPontualTempo)}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${tarefaPontualPaused ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700'}`}>
                        {tarefaPontualPaused ? 'Pausa' : 'Em curso'}
                      </span>
                      <ChevronRight className="h-5 w-5 text-stone-400" />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Secção 3: Operações Congeladas */}
          {operacoesCongeladas.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase text-stone-400 tracking-wider mb-2 flex items-center gap-1.5">
                <Snowflake className="h-3.5 w-3.5 text-blue-500" />
                Operacoes Congeladas ({operacoesCongeladas.length})
              </p>
              {operacoesCongeladas.map((op) => {
                const stale = isStale(op);
                return (
                  <Card
                    key={op.registo_id || op.id}
                    className={`overflow-hidden transition-all mb-3 ${
                      stale
                        ? 'border-l-4 border-l-red-400 bg-red-50/50'
                        : 'border-l-4 border-l-blue-400 bg-gradient-to-r from-blue-50 to-white hover:shadow-lg cursor-pointer'
                    }`}
                    onClick={() => !stale && onDescongelar && onDescongelar(op)}
                    data-testid={`congelada-card-${op.registo_id}`}
                  >
                    <div className={`p-3 border-b ${stale ? 'bg-red-100/50 border-red-200' : 'bg-blue-100/50 border-blue-200'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-navy text-lg">OF {op.of_numero || 'N/A'}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            stale ? 'bg-red-200 text-red-700' : 'bg-blue-200 text-blue-700'
                          }`}>
                            {stale ? 'Dados removidos' : 'Congelada'}
                          </span>
                        </div>
                        {stale ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDescartar && onDescartar(op.registo_id);
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors"
                            data-testid={`descartar-congelada-${op.registo_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                            Apagar
                          </button>
                        ) : (
                          <ChevronRight className="h-5 w-5 text-blue-400" />
                        )}
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="flex items-start gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${stale ? 'bg-red-100' : 'bg-emerald-100'}`}>
                          <Play className={`h-4 w-4 ${stale ? 'text-red-600' : 'text-emerald-600'}`} />
                        </div>
                        <div>
                          <p className="text-xs text-stone-500">OPERACAO</p>
                          <p className="font-semibold text-navy">{op.operacao_codigo || ''} {op.operacao_nome || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-2 bg-stone-50 rounded-lg">
                          <p className="text-xs text-stone-500">CLIENTE</p>
                          <p className="font-medium text-stone-700 truncate">{op.cliente || 'N/A'}</p>
                        </div>
                        <div className="p-2 bg-stone-50 rounded-lg">
                          <p className="text-xs text-stone-500">MODELO</p>
                          <p className="font-medium text-stone-700 truncate">{op.artigo || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-2 bg-amber-50 rounded-lg">
                          <p className="text-xs text-amber-700">LOTE</p>
                          <p className="font-medium text-amber-800">{op.lote_codigo || 'N/A'}</p>
                        </div>
                        <div className="p-2 bg-emerald-50 rounded-lg">
                          <p className="text-xs text-emerald-700">QUANTIDADE</p>
                          <p className="font-bold text-emerald-800">{op.lote_quantidade || 0} un.</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                        <p className="text-xs text-blue-600">
                          <Clock className="h-3 w-3 inline mr-1" />
                          Congelada: {op.congelado_em ? new Date(op.congelado_em).toLocaleString('pt-PT') : 'N/A'}
                        </p>
                        {op.tempo_decorrido > 0 && !modoTestes && (
                          <p className="text-xs text-stone-500">
                            Tempo: {Math.floor(op.tempo_decorrido / 60)}min
                          </p>
                        )}
                      </div>
                      {stale && (
                        <div className="p-2 bg-red-100 rounded-lg text-xs text-red-700">
                          A operacao ou OF associada foi eliminada do sistema. Pode apagar esta entrada.
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Vazio */}
          {!temAlgo && (
            <div className="text-center py-8 text-stone-500">
              <Clock className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Sem operacoes ativas</p>
              <p className="text-sm mt-1">Nenhuma operacao em curso, pausada ou congelada</p>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
